import { useDispatch, useSelector } from 'react-redux';
import ShadingSlider from './ShadingSlider';
import { AppDispatch, RootState } from 'store';
import { 
	ModalContent, ModalShadingInfoWrap, ModalShadingWrap, 
	ShadingPopupMenu, ShadingTitle, ShadingTitleButtons, ShadingWrapper, 
	DivRevertWrap, CardInfoWrap, FacetPropsKeyValue,
	ArrayHeading, FlexTwoCol} from './style';
import Button from 'components/Basic/Button';
import { useMutation } from 'react-query';
import { adjustShading, undoRemoveShading } from 'components/tool/Editor/ButtonDiv/api';
import { setEditShadingPerfomed } from 'store/slices/ToolSlice';
import { changePanel } from 'store/slices/RoofDataSlice/roofDataSlice';
import { appToast } from 'components/Basic/CustomToast';
import { AxiosError } from 'axios';
import { useState, useEffect, useCallback, useRef } from 'react';
import { closeShadingMenu, toggleAdjustShadingWarningModal, toggleShadingMenu, } from 'store/slices/AdjustShadingSlice';
import { AzimuthDiv, PanelsCountDiv, SystemProductionDiv, SystemSize } from 'components/ManualToolModal/DefaultModeValues';
import { SystemDivWrap } from 'components/ManualToolModal/style';

export default function AdjustShadingMenu(){
	const { open, segmentId, shadingVal, isUserMapped, azimuth, pitch, systemProduction, panelsCount, systemSize,
		panelKey, isShadingAdjusted, doneEnabled, totalPanelsProduction, 
		totalPanelsFromFacet, totalSystemSize } = useSelector((state: RootState) => state.adjustShadingState.data.shadingMenu);
	const uuid = useSelector((state: RootState) => state.roofData.data.uuid);
	const designType = useSelector((state: RootState) => state.roofData.data.designType);
	const dispatch = useDispatch<AppDispatch>();
	
	const [shading, setShading] = useState(shadingVal);
	const skipWarningRef = useRef(false);

	useEffect(() => {
		setShading(shadingVal);
	}, [shadingVal]);

	const inputFieldsEdited = shading !== shadingVal;

	const resetToLoadedState = useCallback(() => {
		setShading(shadingVal);
	}, [shadingVal]);

	const adjustShadingQuery = useMutation( async () => {
		const payload = {
			facetId: segmentId as string,
			panelKey: panelKey,
			isUserMapped: isUserMapped,
			shading: Math.round(shading || 0),
		};
		await adjustShading(uuid, payload);
		dispatch(setEditShadingPerfomed({editShadingPerformed: true}));
		await dispatch(changePanel({ uuid, panelKey: panelKey, designType:designType }));
		dispatch(setEditShadingPerfomed({editShadingPerformed: false}));
	},
	{
		mutationKey: 'adjust-shading',
		onSuccess(data) {
			skipWarningRef.current = true;
			console.log(data);
			dispatch(closeShadingMenu());
		},
		onError(error: AxiosError<{ message: string }>) {
			if (error.response?.data) {
				appToast.error(error.response.data?.message || error.message);
			}
		}
	});

	const removeShadingQuery = useMutation( async () => {
		const payload = {
			facetId: segmentId as string,
			panelKey: panelKey,
			isUserMapped: isUserMapped
		};
		await undoRemoveShading(uuid, payload);
		dispatch(setEditShadingPerfomed({editShadingPerformed: true}));
		await dispatch(changePanel({ uuid, panelKey: panelKey, designType:designType }));
		dispatch(setEditShadingPerfomed({editShadingPerformed: false}));
	},
	{
		mutationKey: 'remove-shading',
		onSuccess() {
			skipWarningRef.current = true;
			console.log('Remove shading performed');
			setShading(0);
			dispatch(closeShadingMenu());
		},
		onError(error: AxiosError<{ message: string }>) {
			if (error.response?.data) {
				appToast.error(error.response.data?.message || error.message);
			}
		}
	});

	return (
		<ShadingPopupMenu
			open={open}
			closeOnDocumentClick={true}
			onClose={() => {
				if (skipWarningRef.current) {
					skipWarningRef.current = false;
					dispatch(closeShadingMenu());
					return;
				}
				if (inputFieldsEdited) {
					dispatch(toggleAdjustShadingWarningModal(
						{ 
							open: true, 
							warningMessage: 'Do you want to reset the changes and exit this menu?',
						}
					));
					dispatch(toggleShadingMenu({open: false}));
				} else {
					dispatch(closeShadingMenu());
				}
			}}
			className={open ? 'open' : 'close'}
		>
			<div className="popup-modal-wrap">
				<ModalContent className="modal-content ">
					<ModalShadingWrap>
						<ShadingWrapper>
							<ShadingTitle>Adjust Shading:</ShadingTitle>
							<ShadingTitleButtons>
								{inputFieldsEdited && (
									<Button
										onClick={resetToLoadedState}
										id='reset-shading-btn'
										disabled={adjustShadingQuery.isLoading || removeShadingQuery.isLoading}
										className='btn-reset no-focus-outline'>
										<svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path d="M8.5 10.4853H11.1069M13.1123 8.94116V10.4853M13.1123 12.0294V10.4853M13.1123 10.4853H14.6764" 
												stroke="var(--defaultIconColor)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
											<path d="M14.6758 14.456H12.0688M10.0635 12.9119V14.456M10.0635 16.0001V14.456M10.0635 14.456H8.49933" 
												stroke="var(--defaultIconColor)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
											<path d="M5.7317 13.5996C3.01246 12.8991 1 10.3877 1 7.39704C1 3.86405 3.80845 1 7.27284 1C10.2025 
												1 12.6631 3.0482 13.3529 5.81707M5.7317 13.5996V10.8863M5.7317 13.5996V13.7941H2.71077" 
											stroke="var(--defaultIconColor)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
											Reset
									</Button>
								)}
								<Button
									id='done-shading-btn'
									className='no-focus-outline'
									showLoader={adjustShadingQuery.isLoading}
									disabled={(adjustShadingQuery.isLoading || removeShadingQuery.isLoading)
										|| (doneEnabled ? false : !inputFieldsEdited)}
									onClick={() => adjustShadingQuery.mutate()}
								>
										Done
								</Button>
							</ShadingTitleButtons>
						</ShadingWrapper>
						<ModalShadingInfoWrap className="modal-facet-info-wrap modal-adjust-shadow-wrap">
							<CardInfoWrap className={`active ${inputFieldsEdited ? 'edited' : ''}`}>
								<FlexTwoCol>
									<AzimuthDiv azimuthDegrees={azimuth ?? 0} />
									<FacetPropsKeyValue>
										<label>Pitch:</label>
										<span className="value">{Math.round(pitch)* 100 / 100}</span>
									</FacetPropsKeyValue>
								</FlexTwoCol>
								<FlexTwoCol>
									<PanelsCountDiv panelsCount={panelsCount ?? 0} />
									<SystemSize systemSize={systemSize ?? 0} />
								</FlexTwoCol>
								<SystemProductionDiv systemProduction={systemProduction ?? 0} />
								<ShadingSlider
									shading={+(shading ?? 0).toFixed()}
									active={true}
									onChange={(val) => {
										setShading(+val);
									}}
									disabled={false}
								/>
								{(panelsCount != totalPanelsFromFacet) && 
								<SystemDivWrap>
									<ArrayHeading>Complete array details</ArrayHeading> 
									<SystemProductionDiv className='array-detail-value' systemProduction={totalPanelsProduction ?? 0} />
									<SystemSize className='array-detail-value' systemSize={totalSystemSize ?? 0} />
									<PanelsCountDiv className='array-detail-value' panelsCount={totalPanelsFromFacet ?? 0} />
								</SystemDivWrap>
								}	
							</CardInfoWrap>
							{isShadingAdjusted && <DivRevertWrap>
								<Button
									showLoader={removeShadingQuery.isLoading}
									disabled={removeShadingQuery.isLoading || adjustShadingQuery.isLoading} 
									onClick={() => removeShadingQuery.mutate()}
									className='btn-reset'
								>Revert to auto detection</Button>
							</DivRevertWrap>}
						</ModalShadingInfoWrap>
						
					</ModalShadingWrap>
				</ModalContent>
			</div>
		</ShadingPopupMenu>
	);
}